import { IAs2Partner } from '@core/models/as2-message.model';
import React, { FC, useState } from 'react';
import { toast } from 'react-toastify';
import as2IntegrationService from 'services/api/as2-integration.service';
import integrationsService from 'services/api/integrations.service';
import { downloadReferenceCertificate } from 'Views/Integrations/cert-upload.helpers';
import {
    ConnectionTestResult,
    formatConnectionTestResult,
    integrationErrorMessage,
} from 'Views/Integrations/integration-user-messages';

type Props = {
    partner: IAs2Partner;
    contactEmail?: string;
    onPartnerUpdated?: () => void;
    onEdit?: (partner: IAs2Partner) => void;
    onTestComplete?: (code: string, result: ConnectionTestResult) => void;
    compact?: boolean;
};

const As2PartnerQuickActions: FC<Props> = ({
    partner,
    contactEmail,
    onPartnerUpdated,
    onEdit,
    onTestComplete,
    compact = false,
}) => {
    const [testing, setTesting] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [toggling, setToggling] = useState(false);

    const code = partner.code!;

    const testConnection = async () => {
        setTesting(true);
        try {
            const r: any = await as2IntegrationService.testConnection(code);
            const result = r.data as ConnectionTestResult;
            onTestComplete?.(code, result);
            const formatted = formatConnectionTestResult(code, result);
            if (formatted.kind === 'success') {
                toast.success(formatted.message, { autoClose: 8000 });
            } else {
                toast.error(formatted.message, { autoClose: 10000 });
            }
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not run connection test.'), { autoClose: 5000 });
        } finally {
            setTesting(false);
        }
    };

    const toggleEnabled = async () => {
        setToggling(true);
        try {
            await as2IntegrationService.setEnabled(code, !partner.enabled);
            toast.success(`${code} ${partner.enabled ? 'disabled' : 'enabled'}`, { autoClose: 5000 });
            onPartnerUpdated?.();
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not update partner.'), { autoClose: 5000 });
        } finally {
            setToggling(false);
        }
    };

    const sendOnboarding = async () => {
        setNotifying(true);
        try {
            await integrationsService.notifyOnboarding(code);
            toast.success(`Onboarding email sent for ${code}`, { autoClose: 5000 });
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not send onboarding email.'), { autoClose: 8000 });
        } finally {
            setNotifying(false);
        }
    };

    const btnClass = compact
        ? 'text-xs text-slate-700 hover:underline disabled:opacity-50'
        : 'rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50';

    return (
        <div className={`flex flex-wrap gap-1.5 ${compact ? '' : 'gap-2'}`}>
            <button type="button" className={btnClass} disabled={testing} onClick={testConnection}>
                {testing ? 'Testing…' : 'Test URL'}
            </button>
            <button type="button" className={btnClass} disabled={toggling} onClick={toggleEnabled}>
                {toggling ? '…' : partner.enabled ? 'Disable' : 'Enable'}
            </button>
            <button type="button" className={btnClass} onClick={() => downloadReferenceCertificate(code)}>
                Cert
            </button>
            {(contactEmail || partner.contactEmail) && (
                <button type="button" className={btnClass} disabled={notifying} onClick={sendOnboarding}>
                    {notifying ? 'Sending…' : 'Email'}
                </button>
            )}
            {onEdit && (
                <button type="button" className={btnClass} onClick={() => onEdit(partner)}>
                    Edit
                </button>
            )}
        </div>
    );
};

export default As2PartnerQuickActions;
